import { useMsal } from '@azure/msal-react';
import { useQuery } from '@tanstack/react-query';
import type { Role } from '@commissions/auth/roles';
import { apiJson, HttpError } from './request-service';

/**
 * What the caller reaches, resolved by the SERVER (Q23/Q31). Ids only — the
 * sidebar is derived by filtering the shared page registry:
 *
 *     PAGES.filter(p => p.nav && grants.pages.includes(p.id))
 *
 * Deliberately carries no `view`/`edit` units: `_access` answers per-field
 * questions per response, and a role-level unit list would be a second, weaker
 * answer competing with it.
 */
export type Grants = {
  pages: string[];
  tabs: string[];
  /**
   * ACTIONS the caller may perform, by operation-registry id (Q34) — e.g.
   * `add_note`. An operation has no field-shaped question behind it, so
   * `_access` cannot answer for it and this is the only source.
   */
  operations: string[];
  /**
   * COLLECTIONS the caller may enumerate, qualified `<pageId>.<list>` (Q34).
   *
   * ⚠️ A list id names a PROJECTION, not a collection: two surfaces reading the
   * same rows with different column sets take different ids.
   */
  lists: string[];
};

export type CurrentUser = {
  oid?: string;
  upn?: string;
  name?: string;
  tid?: string;
  /**
   * The EFFECTIVE roles for this session — already narrowed by the server when
   * a "view as" subset is selected. Every grant below is computed from these.
   */
  roles: Role[];
  /**
   * What the TOKEN actually carries, before any "view as" narrowing.
   *
   * The picker renders its options from this, not from `roles`: `roles` shrinks
   * to the current selection, so a picker driven by it would lose its own
   * options on first use and could never widen back without a reload.
   *
   * Equals `roles` when nothing is narrowed.
   */
  heldRoles?: Role[];
  /** True only while a "view as" narrowing is in effect. */
  viewAs?: boolean;
  grants: Grants;
};

/**
 * Fetch /api/me using the MSAL bearer. Two failure modes are distinct and the
 * caller (AppShell) must treat them differently:
 *   - 401 = not authenticated: the token expired or couldn't be acquired
 *     silently. A lapsed session, NOT a role problem — re-authenticate.
 *   - 403 = forbidden (authenticated, but the endpoint denied the privilege).
 *   - 200 with EMPTY `grants.pages` = signed in, but reaching nothing. There is
 *     nowhere to send them, so it is genuine no-access.
 * Only the latter two are genuine "no access"; conflating 401 with them shows
 * an assigned user the misleading "no role assigned" screen after a timeout.
 *
 * ⚠️ Do NOT re-add an `account`-present precondition here.
 *
 * There are two ways to be a caller: a real MSAL session, and the local
 * dev-bypass picker (which holds no MSAL account but sends `x-dev-auth-roles`,
 * so the server answers as the picked role). Gating on `!!account` silently
 * disabled the query for the second one, `data` stayed undefined forever, and
 * AppShell fell back to synthetic ALL-GRANTS — so the SPA believed it reached
 * every tab while the API refused them. That shipped as a visible 403 in the
 * Website tab (2026-08-16). `enabled` is the caller's decision alone.
 *
 * `devRole` is part of the query KEY, not just the header: switching the picker
 * changes who the server answers as, so a cached response belongs to the
 * previous role and must not be reused.
 */
export function useCurrentUser(
  opts: { enabled: boolean; devRole?: string | null; viewAs?: string | null },
) {
  const { instance, accounts } = useMsal();
  const account = accounts[0];

  return useQuery<CurrentUser, HttpError>({
    // `viewAs` is part of the KEY for the same reason `devRole` is: narrowing
    // changes who the server answers as, so a cached response belongs to the
    // previous selection and must not be reused.
    queryKey: ['me', account?.homeAccountId, opts.devRole ?? null, opts.viewAs ?? null],
    queryFn: () => apiJson<CurrentUser>(instance, '/api/me'),
    enabled: opts.enabled,
    retry: (failureCount, err) => {
      // Don't retry auth/permission errors — retrying won't change the outcome.
      // AppShell routes 401 → re-auth and 403/role-less → no-access.
      if (err instanceof HttpError && (err.status === 401 || err.status === 403)) return false;
      return failureCount < 1;
    },
    staleTime: 5 * 60_000,
  });
}
